/**
 * Shared player utilities
 * Used by both client (useLiveStats) and server (parlayUtils, settlement)
 */

// =============================================================================
// Types
// =============================================================================

/** Minimal player stats interface for DNP detection */
export interface PlayerStatsForDNP {
  pts?: number | null;
  reb?: number | null;
  ast?: number | null;
  fg3m?: number | null;
  blk?: number | null;
  stl?: number | null;
  turnover?: number | null;
  // Participation signals: a player who was on the floor but scored 0 still
  // typically attempted a shot, attempted a free throw, or committed a foul.
  fga?: number | null;
  fta?: number | null;
  pf?: number | null;
  // BallDontLie sends min as a string ("12:34"); some box-score types carry it as
  // a number. isPlayerStatsDNP calls .toString() on it, so accept both.
  min?: string | number | null;
}

// =============================================================================
// DNP Detection
// =============================================================================

/**
 * Check if a player's stats indicate DNP (Did Not Play)
 * Shared logic used by:
 * - useLiveStats.ts (frontend live detection)
 * - ballDontLieService.ts (isDNPGame for filtering)
 * - settlementService.ts (settlement DNP check)
 *
 * Logic: Check actual stats FIRST (most reliable), then fall back to minutes.
 * BallDontLie sometimes returns '00' minutes for players who have played.
 */
export function isPlayerStatsDNP(stats: PlayerStatsForDNP | null | undefined): boolean {
  if (!stats) return false;

  // Check if player has any actual stats - most reliable indicator. Includes the
  // participation signals (shot attempts, FT attempts, fouls) so a player who was
  // on the floor but scored 0 is not mistaken for a DNP just because BallDontLie
  // reported '00' minutes for them.
  const hasAnyStats =
    (stats.pts ?? 0) > 0 ||
    (stats.reb ?? 0) > 0 ||
    (stats.ast ?? 0) > 0 ||
    (stats.fg3m ?? 0) > 0 ||
    (stats.blk ?? 0) > 0 ||
    (stats.stl ?? 0) > 0 ||
    (stats.turnover ?? 0) > 0 ||
    (stats.fga ?? 0) > 0 ||
    (stats.fta ?? 0) > 0 ||
    (stats.pf ?? 0) > 0;

  // If player has any stats, they clearly played - not a DNP
  if (hasAnyStats) {
    return false;
  }

  // Player has 0 stats - check if minutes also indicates DNP
  // min is a string like "0:00", "00", "00:00", "12:34", etc.
  const minStr = stats.min?.toString().trim() ?? '';
  if (
    minStr === '' ||
    minStr === '0' ||
    minStr === '00' ||
    minStr === '0:00' ||
    minStr === '00:00'
  ) {
    return true;
  }

  // Player has non-zero minutes but 0 stats - rare edge case, assume played
  return false;
}

// =============================================================================
// Name Matching
// =============================================================================

/**
 * Common nickname mappings for NBA players
 * Maps nickname -> full first name
 */
const NICKNAMES: Record<string, string> = {
  steph: 'stephen',
  mike: 'michael',
  ant: 'anthony',
  sga: 'shai',
  kat: 'karl-anthony',
  pj: 'pj', // No expansion needed
  cj: 'cj',
  rj: 'rj',
  tj: 'tj',
  dj: 'dj',
  aj: 'aj',
};

/**
 * Full names The Odds API writes differently from stats.nba.com. Unlike NICKNAMES, each pair is
 * one player's two whole names, so no other player's match changes. BallDontLie's box spellings
 * are in MISMATCHED_ODDSAPI_TO_BDL_NAMES; its play text can use these (Bub Carrington), so the
 * first-basket compare reads this list too.
 */
export const ODDS_API_TO_NBA_STATS_NAMES: ReadonlyArray<
  readonly [oddsApi: string, nbaStats: string]
> = [
  ['Carlton Carrington', 'Bub Carrington'],
  ['Nicolas Claxton', 'Nic Claxton'],
  ['Herb Jones', 'Herbert Jones'],
  ['Ron Holland', 'Ronald Holland II'],
];

/**
 * Normalize a player name for fuzzy matching and comparison
 * Aggressive normalization - removes case, accents, punctuation
 *
 * Examples:
 * - "D'Angelo Russell" -> "dangelo russell"
 * - "P.J. Washington" -> "pj washington"
 * - "Luka Dončić" -> "luka doncic"
 *
 * @param name - Player name to normalize
 * @param preserveCase - If true, preserves original case (for map lookups with proper-case keys)
 * @param stripSuffix - If true, drops a trailing generational suffix (Jr/Sr/II/III/IV).
 *   Default OFF: banned-list and roster-membership matching must keep "X Jr"
 *   distinct from "X" to avoid false collisions. Settlement/roster/mpg callers
 *   that compare across data sources opt in.
 */
export function normalizePlayerNameToMatch(
  name: string,
  preserveCase = false,
  stripSuffix = false,
): string {
  let normalized = name
    .replace(/[.'\u2019]/g, '') // Remove periods and apostrophes (straight and curly)
    .normalize('NFD') // Decompose accented characters
    .replace(/[\u0300-\u036f]/g, '') // Remove accent marks
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();

  if (!preserveCase) {
    normalized = normalized.toLowerCase();
  }

  if (stripSuffix) {
    normalized = normalized.replace(/\s+(jr|sr|ii|iii|iv)$/i, '').trim();
  }

  return normalized;
}

/**
 * Normalize player name for strict matching across providers.
 * Uses shared normalizePlayerNameToMatch (handles accents, apostrophes, periods)
 * then additionally strips generational suffixes (Jr., Sr., II, III, IV).
 */
export function normalizePlayerNameForMatching(name: string): string {
  // First pass: shared normalization (lowercase, strip accents/periods/apostrophes)
  const base = normalizePlayerNameToMatch(name);
  // Second pass: strip generational suffixes
  return base
    .replace(/\s+(jr|sr)(\s|$)/gi, ' ')
    .replace(/\s+(iv|iii|ii)(\s|$)/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Normalize a player name for display purposes
 * Minimal cleaning - preserves case, accents, periods, and suffixes
 *
 * Examples:
 * - "  LeBron James Jr.  " -> "LeBron James Jr."
 * - "Mr. Kevin  Durant" -> "Kevin  Durant"
 * - "P.J. Washington" -> "P.J. Washington" (periods preserved)
 * - "Luka Dončić" -> "Luka Dončić" (accents preserved)
 *
 * @param name - Player name to clean for display
 */
export function normalizePlayerNameForDisplay(name: string): string {
  return name
    .trim()
    .replace(/\s+/g, ' ') // Normalize whitespace
    .replace(/^(mr\.|mrs\.|ms\.|dr\.)\s*/i, ''); // Remove title prefixes only
}

/**
 * @deprecated Use normalizePlayerNameToMatch instead
 */
export function normalizePlayerName(name: string, preserveCase = false): string {
  return normalizePlayerNameToMatch(name, preserveCase);
}

/**
 * Expand nickname to full name if applicable
 */
function expandNickname(name: string): string {
  return NICKNAMES[name] || name;
}

/**
 * Fuzzy match two player names
 * Handles common variations:
 * - "Alex Sarr" vs "Alexandre Sarr" (nickname vs full name)
 * - "J. Tatum" vs "Jayson Tatum" (initial vs full)
 * - "Tatum" vs "Jayson Tatum" (last name only)
 * - "P.J. Washington" vs "PJ Washington" (periods)
 * - "Steph Curry" vs "Stephen Curry" (common nicknames)
 */
export function fuzzyMatchPlayer(
  input: string | null | undefined,
  candidate: string | null | undefined,
): boolean {
  if (!input || !candidate) return false;

  const inputNorm = normalizePlayerName(input);
  const candidateNorm = normalizePlayerName(candidate);

  // Exact match
  if (inputNorm === candidateNorm) return true;

  // Either contains the other
  if (inputNorm.includes(candidateNorm) || candidateNorm.includes(inputNorm)) return true;

  // Split into parts for more granular matching
  const inputParts = inputNorm.split(/\s+/);
  const candidateParts = candidateNorm.split(/\s+/);

  const inputLastName = inputParts[inputParts.length - 1];
  const candidateLastName = candidateParts[candidateParts.length - 1];

  // Last name must match for any further checks
  if (inputLastName === candidateLastName) {
    // Single name matching full name (e.g., "Tatum" vs "Jayson Tatum")
    if (inputParts.length === 1 || candidateParts.length === 1) {
      return true;
    }

    // First initial + last name (e.g., "J. Tatum" matches "Jayson Tatum", "AJ Green" not "Zaay Green")
    const inputFirstName = inputParts[0];
    const candidateFirstName = candidateParts[0];
    if (inputFirstName.length <= 2 || candidateFirstName.length <= 2) {
      return inputFirstName[0] === candidateFirstName[0];
    }

    // Both have full first names - check if similar (e.g., "Alex" in "Alexandre")
    // This prevents "Stephen Curry" matching "Seth Curry"
    if (
      inputFirstName.includes(candidateFirstName) ||
      candidateFirstName.includes(inputFirstName)
    ) {
      return true;
    }

    // Check nickname expansion (e.g., "Steph" -> "Stephen")
    const inputExpanded = expandNickname(inputFirstName);
    const candidateExpanded = expandNickname(candidateFirstName);
    if (inputExpanded === candidateFirstName || candidateExpanded === inputFirstName) {
      return true;
    }
    if (inputExpanded.includes(candidateFirstName) || candidateFirstName.includes(inputExpanded)) {
      return true;
    }
    if (candidateExpanded.includes(inputFirstName) || inputFirstName.includes(candidateExpanded)) {
      return true;
    }
  }

  return false;
}
